import { lazy, Suspense, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Camera, Circle, Eye, Hand, Images, Link as LinkIcon, LogOut, Maximize2, Minimize2, Redo2, Scan, Square, StickyNote, Undo2, Users, X } from "lucide-react";
import * as htmlToImage from "html-to-image";
import { useTherapistBoard } from "../../application/useConstellation";
import PresencePanel from "../components/PresencePanel";
import { useSidebar } from "../../../../context/useSidebar";
import { BoardAction, createNote, createZone } from "../../application/boardReducer";
import { useBoardLinks } from "../../application/useBoardLinks";
import {
    canRedo,
    canUndo,
    historyReducer,
    initHistory,
} from "../../application/historyReducer";
import {
    emptyBoardState,
    FaceExpression,
    FigureAction,
    FigurePreset,
    Puppet,
} from "../../domain/types";
import { figureSpec } from "../../domain/figures";
import { useTherapistFigures } from "../../application/useTherapistFigures";
import ConstellationWorkspace from "../components/ConstellationWorkspace";
import PuppetBuilder from "../components/PuppetBuilder";
import FacePicker from "../components/FacePicker";
import ConstellationSnapshotManager from "../components/ConstellationSnapshotManager";
import SaveSceneModal from "../components/SaveSceneModal";
import { useCenter } from "../../../center";
import ImmersiveToolbar from "../components/ImmersiveToolbar";
import AddAreaModal from "../components/AddAreaModal";
import BoardOverlay from "../components/BoardOverlay";
import BoardStyleModal from "../components/BoardStyleModal";
import LinkEditorModal from "../components/LinkEditorModal";
import { useBoardView } from "../../application/useBoardView";
import { ZONE_COLORS } from "../zoneColors";
import { DEFAULT_TABLE_COLOR, getBoardDefaults } from "../../application/boardDefaults";
import { createConstellationScene } from "../../application/constellationSessions";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import ShareLinkDialog, { type ShareLinkStatus } from "../../../../shared/ui/share/ShareLinkDialog";

const FigureThumbnailBaker = lazy(() => import("../components/FigureThumbnailBaker"));

/**
 * Posted to the call around a framed board when its session ends — kept in
 * step with `TOOL_BOARD_CLOSED_MESSAGE` in the session module, which listens.
 */
const MEETING_BOARD_CLOSED = "alchely:meeting-board-closed";


/**
 * Therapist live view. Edits the board (figures, notes, areas, floor, panorama),
 * shares the one-time link, captures notes, and closes the session.
 *
 * `embedded` is the same board framed inside a call: no app shell around it,
 * so it takes the whole frame, and ending the session hands the stage back to
 * the call instead of navigating the frame to the session's page.
 */
export default function TherapistConstellationPage({ embedded = false }: { embedded?: boolean }) {
    const { boardId } = useParams<{ boardId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // A member of a centre may save a scene to its shared library.
    const { center } = useCenter();
    const {
        board,
        status,
        saveStatus,
        versionRef,
        createLink,
        revokeLink,
        uploadSnapshot,
        renameSnapshot,
        deleteSnapshot,
        saveState,
        close,
        participants,
        kickParticipant,
    } = useTherapistBoard(boardId);
    const [history, hdispatch] = useReducer(historyReducer, emptyBoardState(), initHistory);
    const state = history.present;
    const dispatch = useCallback((action: BoardAction) => hdispatch({ type: "do", action }), []);
    const undo = useCallback(() => hdispatch({ type: "undo" }), []);
    const redo = useCallback(() => hdispatch({ type: "redo" }), []);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    // Bumped (via a new id + counter) to focus the board camera on a figure
    // selected from the participants panel.
    const [focusFigureId, setFocusFigureId] = useState<string | null>(null);
    const focusFigure = useCallback((id: string) => {
        setSelectedId(id);
        // Force a new reference each call so repeated selections re-focus.
        setFocusFigureId(null);
        requestAnimationFrame(() => setFocusFigureId(id));
    }, []);
    const [editingPuppetId, setEditingPuppetId] = useState<string | null>(null);
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [editingZoneId, setEditingZoneId] = useState<string | null>(null);
    // Session notes are only persisted when the board closes, so keep a local
    // draft: a refresh or an accidental tab close mid-session loses nothing.
    const notesDraftKey = boardId ? `constellation.notes.${boardId}` : null;
    const [notes, setNotes] = useState(() => {
        if (!notesDraftKey) return "";
        try {
            return localStorage.getItem(notesDraftKey) ?? "";
        } catch {
            return "";
        }
    });
    const [confirmEndOpen, setConfirmEndOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState<string | null>(null);
    const [shareOpen, setShareOpen] = useState(false);
    const [linkStatus, setLinkStatus] = useState<ShareLinkStatus>("loading");
    const [snapshotState, setSnapshotState] = useState<"idle" | "saving" | "saved" | "error">("idle");
    const [closing, setClosing] = useState<"idle" | "closing" | "closed" | "error">("idle");
    // Save-scene modal: opened on demand from the toolbar.
    const [sceneModal, setSceneModal] = useState<"closed" | "inBoard">("closed");
    const [savingScene, setSavingScene] = useState(false);
    // Board view controls (owner labels, fit-to-board, immersive fullscreen).
    const view = useBoardView();
    // Add-figure picker + add-area (shape/name) overlays (immersive toolbar).
    const [figurePickerOpen, setFigurePickerOpen] = useState(false);
    // Bake the picker's 3D thumbnails only once somebody opens it. Baking runs
    // a second WebGL context and reads every figure back off the GPU; on a
    // fresh browser (a patient joining from a call) that landed on the first
    // seconds of the board, for a picker that shows emoji until it is done.
    const [thumbnailsWanted, setThumbnailsWanted] = useState(false);
    if (figurePickerOpen && !thumbnailsWanted) setThumbnailsWanted(true);
    const [areaModalOpen, setAreaModalOpen] = useState(false);
    const [boardStyleOpen, setBoardStyleOpen] = useState(false);
    // Board mode: "view" = clean observation (tool panel hidden); "participate"
    // = therapist edits the board (tool panel shown).
    const [mode, setMode] = useState<"view" | "participate">("view");
    const [notesOpen, setNotesOpen] = useState(false);
    // The old left panel is gone: participants and captures now open as
    // drawers from the header bar, so the board keeps the full width in both
    // modes.
    const [panel, setPanel] = useState<"none" | "participants" | "snapshots">("none");
    // When the patient's edits last arrived (remote version bump), for the
    // "updated Xs ago" chip.
    const [lastRemoteAt, setLastRemoteAt] = useState<number | null>(null);
    const canvasAreaRef = useRef<HTMLElement>(null);
    // Board-only capture target (canvas + <Html> text overlays) for snapshots.
    const boardCaptureRef = useRef<HTMLDivElement>(null);
    const { collapseSidebar, restoreSidebar } = useSidebar();

    // Therapist's reusable figures (DB-backed). Created/managed from the tool
    // catalogue; here they're only selectable to place on the board.
    const { data: therapistFigures = [] } = useTherapistFigures();

    // Collapse the app's main nav on entry so the therapist gets a clean, wide
    // board, and hand it back on the way out. This does not touch the stored
    // nav preference — the board borrows the width, it does not claim it.
    useEffect(() => {
        collapseSidebar();
        return () => restoreSidebar();
    }, [collapseSidebar, restoreSidebar]);
    // Skip the autosave that the next hydration triggers, so re-applying a
    // remote board does not echo it straight back as a new version (ping-pong).
    const skipSaveRef = useRef(false);

    // Hydrate only on a remote (or initial) version change so our own edits
    // are not clobbered by the poll. On a brand-new, empty board, seed the
    // therapist's default board style (shape + color) into the hydrated state
    // and persist it, so they don't reset it every session (issue #197).
    const defaultsAppliedRef = useRef(false);
    useEffect(() => {
        if (!board || board.version === versionRef.current) return;

        let incoming = board.state;
        let seededDefaults = false;
        if (
            board.version === 0 &&
            !defaultsAppliedRef.current &&
            (board.state.puppets?.length ?? 0) === 0
        ) {
            defaultsAppliedRef.current = true;
            const d = getBoardDefaults();
            const table = board.state.table ?? { shape: "circle", size: 1 };
            if (
                (table.shape ?? "circle") !== d.shape ||
                (table.color ?? DEFAULT_TABLE_COLOR) !== d.color ||
                board.state.ambiance !== d.ambiance
            ) {
                incoming = {
                    ...board.state,
                    ambiance: d.ambiance,
                    table: { ...table, shape: d.shape, color: d.color },
                };
                seededDefaults = true;
            }
        }

        // Skip the echo save for a plain hydration, but PERSIST seeded defaults.
        skipSaveRef.current = !seededDefaults;
        hdispatch({ type: "reset", state: incoming });
        if (versionRef.current >= 0) setLastRemoteAt(Date.now());
        versionRef.current = board.version;
    }, [board, versionRef]);

    // Autosave therapist edits.
    useEffect(() => {
        if (skipSaveRef.current) {
            skipSaveRef.current = false;
            return;
        }
        if (status === "ready") saveState(state);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    // Keyboard: Cmd/Ctrl+Z = undo, +Shift+Z / Ctrl+Y = redo, Escape backs out
    // one level (linking -> selection -> immersive), Delete removes what is
    // selected. Declared after the board handlers it calls (see keyRef below).
    const keyRef = useRef<(e: KeyboardEvent) => void>(() => {});
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => keyRef.current(e);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Tick every 5s so the "updated Xs ago" chip stays fresh.
    const [nowTick, setNowTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setNowTick((n) => n + 1), 5000);
        return () => clearInterval(id);
    }, []);

    // Keep the notes draft on disk while the session runs.
    useEffect(() => {
        if (!notesDraftKey) return;
        try {
            if (notes) localStorage.setItem(notesDraftKey, notes);
            else localStorage.removeItem(notesDraftKey);
        } catch {
            /* private mode / quota — the draft is a convenience, not a promise */
        }
    }, [notes, notesDraftKey]);

    /**
     * Opens the share dialog, minting the link the first time. Minting again
     * would end the link the patient may already hold, so an existing one is
     * shown as it is.
     */
    const mintLink = async () => {
        setLinkStatus("loading");
        try {
            const link = await createLink();
            if (!link) throw new Error("no link");
            setLinkUrl(link.url);
            setLinkStatus("ready");
        } catch {
            setLinkStatus("failed");
        }
    };

    const openShare = () => {
        setShareOpen(true);
        if (!linkUrl) void mintLink();
    };

    const handleRevoke = () => {
        void revokeLink();
        setLinkUrl(null);
        setShareOpen(false);
    };

    const handleSnapshot = async () => {
        const node = boardCaptureRef.current;
        if (!node) {
            setSnapshotState("error");
            return;
        }
        setSnapshotState("saving");
        // Deselect so the selected figure's inline control buttons (edit, rotate,
        // remove…) are hidden and don't end up baked into the snapshot.
        setSelectedId(null);
        try {
            // Let React drop the controls and the WebGL scene paint a frame before
            // we rasterize the board (canvas pixels + the <Html> text overlays).
            await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
            const blob = await htmlToImage.toBlob(node, {
                pixelRatio: 2,
                cacheBust: true,
                // Skip any element opting out of capture (e.g. transient controls).
                filter: (el) =>
                    !(el instanceof HTMLElement && el.dataset.snapshotIgnore === "true"),
            });
            if (!blob) throw new Error("capture failed");
            await uploadSnapshot(blob);
            setSnapshotState("saved");
            setTimeout(() => setSnapshotState("idle"), 2000);
        } catch {
            setSnapshotState("error");
        }
    };

    // Suggested scene name (prefilled, therapist-overwritable) = board title.
    const sceneNameSuggestion = state.title || t("constellation.scenes.title");

    const performClose = async (saveScene?: { name: string; description?: string }) => {
        setClosing("closing");
        try {
            await close(notes, saveScene);
            setClosing("closed");
            if (notesDraftKey) {
                try {
                    localStorage.removeItem(notesDraftKey);
                } catch {
                    /* ignore */
                }
            }
            // Framed inside a call: the call puts the board away.
            if (embedded) {
                window.parent.postMessage({ type: MEETING_BOARD_CLOSED }, window.location.origin);
                return;
            }
            // After finalizing, land on the session detail page (same for
            // individual, couple and anonymous — the board carries session_id).
            if (board?.session_id) {
                navigate(`/session/${board.session_id}`);
            } else if (window.history.length > 1) {
                navigate(-1);
            }
        } catch {
            setClosing("error");
        }
    };

    const handleEndSession = () => {
        if (closing === "closing") return;
        // Leave OS fullscreen so the confirm dialog shows.
        view.dropNativeFullscreen();
        setConfirmEndOpen(true);
    };

    const confirmEndSession = async () => {
        setConfirmEndOpen(false);
        // Scenes are saved on demand via the "Save scene" toolbar action, so the
        // finalize flow just closes the board (issue #197).
        await performClose();
    };

    // Save the current board as a scene without closing (in-board action).
    const handleSceneModalConfirm = async (
        name: string,
        description: string,
        shareWithCenter: boolean,
    ) => {
        if (!boardId) return;
        setSavingScene(true);
        try {
            await createConstellationScene({
                name,
                description: description || undefined,
                board_id: boardId,
                share_with_center: shareWithCenter || undefined,
            });
            setSceneModal("closed");
        } finally {
            setSavingScene(false);
        }
    };

    const handleSceneModalCancel = () => setSceneModal("closed");

    // --- board editing handlers ---
    const addPuppet = (puppet: Puppet) => dispatch({ type: "addPuppet", puppet });
    const movePuppet = (id: string, x: number, z: number) =>
        dispatch({ type: "movePuppet", id, position: { x, y: 0, z } });
    const rotatePuppet = (id: string, delta: number) => {
        const cur = state.puppets.find((p) => p.id === id);
        if (cur) dispatch({ type: "rotatePuppet", id, rotationY: cur.rotationY + delta });
    };
    const removePuppet = (id: string) => {
        dispatch({ type: "removePuppet", id });
        if (selectedId === id) setSelectedId(null);
    };
    const renamePuppet = (id: string, name: string) =>
        dispatch({ type: "renamePuppet", id, name });
    const setPuppetFace = (id: string, face: FaceExpression) =>
        dispatch({ type: "updatePuppetAttributes", id, face });
    const setPuppetNote = (id: string, note: string) => dispatch({ type: "setNote", id, note });
    const setPuppetAction = (id: string, action: FigureAction) =>
        dispatch({ type: "setAction", id, action });
    const openEdit = (id: string) => {
        setSelectedId(id);
        setEditingPuppetId(id);
    };

    const addNote = () => {
        const note = createNote("", { x: 0, y: 0, z: 0 });
        dispatch({ type: "addNote", note });
        setSelectedId(note.id);
        setEditingNoteId(note.id);
    };
    const moveNote = (id: string, x: number, z: number) =>
        dispatch({ type: "moveNote", id, position: { x, y: 0, z } });
    const removeNote = (id: string) => {
        dispatch({ type: "removeNote", id });
        if (editingNoteId === id) setEditingNoteId(null);
    };
    const editingNote = (state.notes ?? []).find((n) => n.id === editingNoteId) ?? null;

    const addZone = (opts?: { shape?: "circle" | "square"; label?: string }) => {
        const zone = createZone(opts?.label ?? "", {
            color: ZONE_COLORS[(state.zones ?? []).length % ZONE_COLORS.length],
            ...(opts?.shape ? { shape: opts.shape } : {}),
        });
        dispatch({ type: "addZone", zone });
        setSelectedId(zone.id);
        if (!opts) setEditingZoneId(zone.id);
    };
    const moveZone = (id: string, x: number, z: number) =>
        dispatch({ type: "moveZone", id, position: { x, y: 0, z } });
    const removeZone = (id: string) => {
        dispatch({ type: "removeZone", id });
        if (editingZoneId === id) setEditingZoneId(null);
    };
    const resizeZone = (id: string, dims: { radius?: number; width?: number; depth?: number }) =>
        dispatch({ type: "editZone", id, ...dims });
    const editingZone = (state.zones ?? []).find((z) => z.id === editingZoneId) ?? null;

    // Links between figures (shared state machine with the patient board).
    const links = useBoardLinks({
        state,
        dispatch,
        selectedId,
        setSelectedId,
        onEnterLinkMode: () => setEditingPuppetId(null),
    });
    const editingLink = links.editingLink;

    const editingPuppet = state.puppets.find((p) => p.id === editingPuppetId) ?? null;
    // Actions + emotions apply to humans and to custom avatars.
    const editingIsHuman =
        editingPuppet !== null &&
        (figureSpec(editingPuppet.figure).category === "human" ||
            editingPuppet.figure === FigurePreset.AVATAR);

    // Board keyboard shortcuts. Assigned every render so the handler always
    // sees fresh state without re-binding the window listener.
    const anyOverlayOpen =
        editingPuppetId !== null ||
        editingNoteId !== null ||
        editingZoneId !== null ||
        editingLink !== null ||
        figurePickerOpen ||
        areaModalOpen ||
        boardStyleOpen ||
        notesOpen ||
        panel !== "none" ||
        confirmEndOpen ||
        sceneModal !== "closed";
    useEffect(() => {
        keyRef.current = (e: KeyboardEvent) => {
            const el = e.target as HTMLElement | null;
            const typing =
                !!el &&
                (el.tagName === "INPUT" ||
                    el.tagName === "TEXTAREA" ||
                    el.isContentEditable);

            if (e.metaKey || e.ctrlKey) {
                if (typing) return;
                const key = e.key.toLowerCase();
                if (key === "z") {
                    e.preventDefault();
                    if (e.shiftKey) redo();
                    else undo();
                } else if (key === "y") {
                    e.preventDefault();
                    redo();
                }
                return;
            }

            // Escape backs out one level at a time; overlays close themselves.
            if (e.key === "Escape") {
                if (typing || anyOverlayOpen) return;
                if (links.linkingFromId) links.cancelLinking();
                else if (selectedId) setSelectedId(null);
                else if (view.immersive) view.exitImmersive();
                return;
            }

            if (e.key === "Delete" || e.key === "Backspace") {
                if (typing || anyOverlayOpen) return;
                if (mode !== "participate" || !selectedId) return;
                e.preventDefault();
                if (state.puppets.some((p) => p.id === selectedId)) removePuppet(selectedId);
                else if ((state.notes ?? []).some((n) => n.id === selectedId))
                    removeNote(selectedId);
                else if ((state.zones ?? []).some((z) => z.id === selectedId))
                    removeZone(selectedId);
            }
        };
    });

    // Shortcut hint prefix for tooltips (⌘ on macOS, Ctrl elsewhere).
    const undoHint =
        typeof navigator !== "undefined" && /Mac|iP(hone|ad|od)/.test(navigator.platform)
            ? "⌘"
            : "Ctrl+";

    // Relative "updated Xs ago" (recomputed each tick).
    const syncAgoLabel = (() => {
        void nowTick;
        if (lastRemoteAt == null) return null;
        const s = Math.max(0, Math.round((Date.now() - lastRemoteAt) / 1000));
        if (s < 5) return t("constellation.therapist.sync.justNow");
        if (s < 60) return t("constellation.therapist.sync.secondsAgo", { count: s });
        return t("constellation.therapist.sync.minutesAgo", { count: Math.round(s / 60) });
    })();

    // Inside the app shell the header and the shell's padding take 8rem; a
    // framed board has the whole frame.
    const pageHeight = embedded ? "h-[100dvh]" : "h-[calc(100vh-8rem)]";
    // Inside a call the session goes on after the board: what ends here is
    // the constellation, and the button says so.
    const endKey = embedded ? "constellation.therapist.endBoard" : "constellation.therapist.endSession";

    if (closing === "closed") {
        return (
            <div className={`flex ${pageHeight} flex-col items-center justify-center gap-3 p-6 text-center`}>
                <span className="text-5xl" aria-hidden>
                    ✨
                </span>
                <h1 className="text-xl font-semibold text-gray-800">
                    {t("constellation.therapist.endSessionDoneTitle")}
                </h1>
                <p className="max-w-sm text-sm text-gray-500">
                    {t("constellation.therapist.endSessionDoneBody")}
                </p>
            </div>
        );
    }

    return (
        <div className={`flex ${pageHeight} flex-col`}>
            {thumbnailsWanted && (
                <Suspense fallback={null}>
                    <FigureThumbnailBaker />
                </Suspense>
            )}
            {/* Session header — share link, participants, captures and the
                finalize action live above the board, so they stay reachable in
                both modes and the board keeps the full width (no side panel). */}
            {!view.immersive && (
                <header className="flex flex-wrap items-center gap-2 border-b border-border-subtle bg-surface-raised px-4 py-2">
                    <h1 className="mr-2 text-sm font-semibold text-content-primary">
                        {t("constellation.therapist.title")}
                    </h1>

                    <button
                        type="button"
                        onClick={openShare}
                        className="flex min-h-[40px] items-center gap-1.5 rounded-md bg-accent px-3 text-sm font-medium text-white hover:bg-accent-hover"
                    >
                        <LinkIcon className="h-4 w-4" />
                        {t("constellation.therapist.createLink")}
                    </button>

                    <div className="ml-auto flex items-center gap-2">
                        {board?.session_type !== "individual" && board?.session_type != null && (
                            <button
                                type="button"
                                onClick={() =>
                                    setPanel((p) => (p === "participants" ? "none" : "participants"))
                                }
                                aria-pressed={panel === "participants"}
                                title={t("constellation.therapist.participantsTitle", "Participants")}
                                className={`flex min-h-[40px] items-center gap-1.5 rounded-md border px-2.5 text-sm transition-colors ${
                                    panel === "participants"
                                        ? "border-brand-500 bg-brand-600 text-white"
                                        : "border-border-subtle hover:bg-surface-sunken"
                                }`}
                            >
                                <Users className="h-4 w-4" />
                                <span className="hidden sm:inline">
                                    {t("constellation.therapist.participantsTitle", "Participants")}
                                </span>
                                <span className="text-xs opacity-70">{participants.length}</span>
                            </button>
                        )}

                        {board && board.snapshots.length > 0 && (
                            <button
                                type="button"
                                onClick={() =>
                                    setPanel((p) => (p === "snapshots" ? "none" : "snapshots"))
                                }
                                aria-pressed={panel === "snapshots"}
                                title={t("constellation.therapist.snapshotsTitle")}
                                className={`flex min-h-[40px] items-center gap-1.5 rounded-md border px-2.5 text-sm transition-colors ${
                                    panel === "snapshots"
                                        ? "border-brand-500 bg-brand-600 text-white"
                                        : "border-border-subtle hover:bg-surface-sunken"
                                }`}
                            >
                                <Images className="h-4 w-4" />
                                <span className="hidden sm:inline">
                                    {t("constellation.therapist.snapshotsTitle")}
                                </span>
                                <span className="text-xs opacity-70">{board.snapshots.length}</span>
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={handleEndSession}
                            disabled={closing === "closing"}
                            className="flex min-h-[40px] items-center gap-1.5 rounded-md bg-gray-800 px-3 text-sm font-medium text-white hover:bg-gray-900 disabled:opacity-50"
                        >
                            <LogOut className="h-4 w-4" />
                            {closing === "closing"
                                ? t("constellation.therapist.endSessionClosing")
                                : t(endKey)}
                        </button>
                    </div>

                    {closing === "error" && (
                        <p role="alert" className="w-full text-xs text-red-600">
                            {t("constellation.therapist.endSessionError")}
                        </p>
                    )}
                </header>
            )}

            <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            <main
                ref={(el) => {
                    canvasAreaRef.current = el;
                    view.containerRef.current = el;
                }}
                className={
                    view.immersive
                        ? "fixed inset-0 z-[60] min-w-0 bg-slate-900"
                        : "relative min-w-0 flex-1"
                }
            >
                {links.linkingFromId && (
                    <div className="absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-sm font-medium text-white shadow">
                        {t("constellation.link.pickTarget")}
                        <button
                            type="button"
                            onClick={links.cancelLinking}
                            className="rounded-full bg-white/20 px-2 py-0.5 text-xs hover:bg-white/30"
                        >
                            {t("common.cancel", "Cancel")}
                        </button>
                    </div>
                )}
                {state.title && !links.linkingFromId ? (
                    <div className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-full bg-white/90 px-4 py-1.5 text-sm font-semibold text-gray-800 shadow-theme-sm">
                        {state.title}
                    </div>
                ) : null}

                {/* Alchely brand mark, top-left of the board (immersive only).
                    White unconditionally: immersive is slate-900 whatever the
                    therapist's theme says. */}
                {view.immersive && (
                    <img
                        src="/images/logo/logo.png"
                        alt="Alchely"
                        className="pointer-events-none absolute left-4 top-4 z-[64] h-8 opacity-90 brightness-0 invert"
                    />
                )}

                {/* Sync / presence chip */}
                <div
                    className={`pointer-events-none absolute left-3 z-20 flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-xs shadow-theme-sm ${
                        view.immersive ? "top-14" : "top-3"
                    }`}
                >
                    <span
                        className={`h-2 w-2 rounded-full ${
                            status === "ready"
                                ? "bg-green-500"
                                : status === "error"
                                  ? "bg-red-500"
                                  : "bg-gray-300"
                        }`}
                        aria-hidden
                    />
                    <span className="font-medium text-gray-700">
                        {status === "ready"
                            ? t("constellation.therapist.sync.live")
                            : status === "error"
                              ? t("constellation.therapist.sync.offline")
                              : t("constellation.therapist.sync.connecting")}
                    </span>
                    {syncAgoLabel && (
                        <span className="text-content-tertiary">· {syncAgoLabel}</span>
                    )}
                    {saveStatus === "saving" && (
                        <span className="text-content-tertiary">· {t("constellation.therapist.sync.saving")}</span>
                    )}
                    {saveStatus === "saved" && (
                        <span className="text-green-600">· {t("constellation.therapist.sync.saved")}</span>
                    )}
                    {saveStatus === "error" && (
                        <span className="text-red-600">· {t("constellation.therapist.sync.saveError")}</span>
                    )}
                </div>

                {/* Board controls: undo/redo (participate) + notes + capture + mode */}
                <div
                    role="toolbar"
                    aria-label={t("constellation.therapist.boardControls", "Board controls")}
                    aria-orientation="horizontal"
                    className="absolute right-3 top-3 z-20 flex items-center gap-2"
                >
                    {mode === "participate" && (
                        <div className="flex items-center gap-1 rounded-md border border-gray-200 bg-white/95 shadow-theme-sm">
                            <button
                                type="button"
                                onClick={undo}
                                disabled={!canUndo(history)}
                                aria-label={t("constellation.history.undo")}
                                title={`${t("constellation.history.undo")} (${undoHint}Z)`}
                                className="rounded-l-md p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                            >
                                <Undo2 className="h-5 w-5" />
                            </button>
                            <button
                                type="button"
                                onClick={redo}
                                disabled={!canRedo(history)}
                                aria-label={t("constellation.history.redo")}
                                title={`${t("constellation.history.redo")} (${undoHint}⇧Z)`}
                                className="rounded-r-md p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                            >
                                <Redo2 className="h-5 w-5" />
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => setNotesOpen((v) => !v)}
                        aria-label={t("constellation.therapist.notes")}
                        title={t("constellation.therapist.notes")}
                        aria-pressed={notesOpen}
                        className={`rounded-md border p-2 shadow-theme-sm transition-colors ${
                            notesOpen
                                ? "border-yellow-300 bg-yellow-100 text-yellow-900"
                                : "border-gray-200 bg-white/95 text-gray-600 hover:bg-gray-100"
                        }`}
                    >
                        <StickyNote className="h-5 w-5" />
                    </button>
                    <div className="flex flex-col items-end">
                        <button
                            type="button"
                            onClick={handleSnapshot}
                            disabled={snapshotState === "saving" || status !== "ready"}
                            aria-label={t("constellation.therapist.takeSnapshot")}
                            title={t("constellation.therapist.takeSnapshot")}
                            className="rounded-md border border-brand-200 bg-white/95 p-2 text-brand-700 shadow-theme-sm hover:bg-brand-50 disabled:opacity-50"
                        >
                            <Camera className="h-5 w-5" />
                        </button>
                        {snapshotState === "saved" && (
                            <span className="mt-1 rounded bg-green-600/90 px-1.5 py-0.5 text-xs font-medium text-white shadow">
                                {t("constellation.therapist.snapshotSaved")}
                            </span>
                        )}
                        {snapshotState === "error" && (
                            <span
                                role="alert"
                                className="mt-1 rounded bg-red-600/90 px-1.5 py-0.5 text-xs font-medium text-white shadow"
                            >
                                {t("constellation.therapist.snapshotError")}
                            </span>
                        )}
                    </div>
                    {/* Segmented control: the highlighted half is the mode you
                        are IN, so the board never reads as the opposite state. */}
                    <div className="flex items-center rounded-md border border-gray-200 bg-white/95 p-0.5 shadow-theme-sm">
                        {(["view", "participate"] as const).map((m) => {
                            const active = mode === m;
                            const label = t(
                                m === "view"
                                    ? "constellation.therapist.viewMode"
                                    : "constellation.therapist.participateMode",
                            );
                            const Icon = m === "view" ? Eye : Hand;
                            return (
                                <button
                                    key={m}
                                    type="button"
                                    onClick={() => setMode(m)}
                                    aria-pressed={active}
                                    aria-label={label}
                                    title={label}
                                    className={`flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm font-medium transition-colors ${
                                        active
                                            ? "bg-brand-600 text-white"
                                            : "text-gray-600 hover:bg-gray-100"
                                    }`}
                                >
                                    <Icon className="h-5 w-5" />
                                    <span className="hidden sm:inline">{label}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Board view controls: fit, owner labels, fullscreen — on the
                        same line as notes/screenshot/mode (issue #197). Fit lives
                        on the bottom action bar while editing, so it is not
                        offered twice at once. */}
                    {mode === "view" && (
                        <button
                            type="button"
                            onClick={view.requestFit}
                            aria-label={t("constellation.workspace.fitToBoard")}
                            title={t("constellation.workspace.fitToBoard")}
                            className="rounded-md border border-gray-200 bg-white/95 p-2 text-gray-600 shadow-theme-sm hover:bg-gray-100"
                        >
                            <Scan className="h-5 w-5" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={view.toggleOwners}
                        aria-pressed={view.showOwners}
                        aria-label={t("constellation.workspace.toggleOwners")}
                        title={t("constellation.workspace.toggleOwners")}
                        className={`rounded-md border p-2 shadow-theme-sm transition-colors ${
                            view.showOwners
                                ? "border-brand-500 bg-brand-600 text-white"
                                : "border-gray-200 bg-white/95 text-gray-600 hover:bg-gray-100"
                        }`}
                    >
                        <Users className="h-5 w-5" />
                    </button>
                    <button
                        type="button"
                        onClick={view.toggleImmersive}
                        aria-pressed={view.immersive}
                        aria-label={t("constellation.workspace.immersive")}
                        title={t("constellation.workspace.immersive")}
                        className="rounded-md border border-gray-200 bg-white/95 p-2 text-gray-600 shadow-theme-sm hover:bg-gray-100"
                    >
                        {view.immersive ? (
                            <Minimize2 className="h-5 w-5" />
                        ) : (
                            <Maximize2 className="h-5 w-5" />
                        )}
                    </button>
                </div>

                {/* The board action bar (below) replaces the old floating tool
                    panel in participate mode (issue #197). */}
                {/* Capture target: only the board (canvas + <Html> text labels),
                    so snapshots include figures AND their note/name/zone text but
                    not the surrounding UI chrome (chips, tool panel, buttons). */}
                <div ref={boardCaptureRef} className="absolute inset-0">
                    <ConstellationWorkspace
                        state={state}
                        loading={status === "loading"}
                        selectedId={selectedId}
                        onSelect={setSelectedId}
                        onMove={movePuppet}
                        onRotate={rotatePuppet}
                        onRemove={removePuppet}
                        onEdit={openEdit}
                        onNoteMove={moveNote}
                        onNoteEdit={(id) => {
                            setSelectedId(id);
                            setEditingNoteId(id);
                        }}
                        onNoteRemove={removeNote}
                        onZoneMove={moveZone}
                        onZoneEdit={(id) => {
                            setSelectedId(id);
                            setEditingZoneId(id);
                        }}
                        onZoneRemove={removeZone}
                        onZoneResize={resizeZone}
                        onStartLink={links.startLink}
                        linkingFromId={links.linkingFromId}
                        onLinkTarget={links.linkTarget}
                        onLinkEdit={links.openLinkEdit}
                        onLinkRemove={links.removeLink}
                        viewer={{ isTherapist: true, participantId: null }}
                        therapistLabel={t("constellation.owners.therapist")}
                        focusFigureId={focusFigureId}
                        showOwners={view.showOwners}
                        fitRequest={view.fitRequest}
                    />
                </div>

                {/* Board action bar — shown whenever the therapist is editing
                    (participate mode), and in immersive/fullscreen. Replaces the
                    floating tool panel (issue #197). */}
                {mode === "participate" && (
                    <ImmersiveToolbar
                        onAddFigure={() => setFigurePickerOpen(true)}
                        onAddNote={addNote}
                        onAddArea={() => setAreaModalOpen(true)}
                        onFit={view.requestFit}
                        onSaveScene={() => setSceneModal("inBoard")}
                        saveSceneDisabled={status !== "ready" || state.puppets.length === 0}
                        onExit={view.immersive ? view.exitImmersive : undefined}
                        onToggleNativeFullscreen={view.toggleNativeFullscreen}
                        nativeFullscreen={view.nativeFullscreen}
                        disabled={status !== "ready"}
                        onBoardStyle={() => setBoardStyleOpen(true)}
                    />
                )}
            </main>
            </div>

            {/* Add-figure picker — opens the figure grid directly, inside the
                board container so it shows over the immersive/fullscreen view. */}
            {figurePickerOpen && (
                <PuppetBuilder
                    startOpen
                    hideTrigger
                    onClose={() => setFigurePickerOpen(false)}
                    onAdd={addPuppet}
                    disabled={status !== "ready"}
                    therapistFigures={therapistFigures}
                    portalContainer={view.containerRef.current}
                />
            )}

            {/* Session notes — collapsible side drawer (toggled from the board).
                Portalled into the board container so it shows over the immersive
                and OS-fullscreen board (issue #197). */}
            {notesOpen && (
                <BoardOverlay container={view.containerRef.current}>
                    <div
                        className="fixed inset-0 z-[80] bg-black/20"
                        onClick={() => setNotesOpen(false)}
                        aria-hidden
                    />
                    <aside
                        className="fixed right-0 top-0 z-[81] flex h-full w-full max-w-md flex-col border-l bg-white shadow-2xl"
                        role="dialog"
                        aria-label={t("constellation.therapist.notes")}
                    >
                        <header className="flex h-14 shrink-0 items-center justify-between border-b px-4">
                            <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                                <StickyNote className="h-4 w-4 text-content-tertiary" />
                                {t("constellation.therapist.notes")}
                            </span>
                            <button
                                type="button"
                                onClick={() => setNotesOpen(false)}
                                aria-label={t("common.close", "Close")}
                                className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </header>
                        <textarea
                            autoFocus
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="min-h-0 flex-1 resize-none p-4 text-sm outline-none"
                            placeholder={t("constellation.therapist.notesPlaceholder")}
                        />
                    </aside>
                </BoardOverlay>
            )}

            {/* Participants / captures — the two panels the old left column
                held, now drawers opened from the header. */}
            {panel !== "none" && (
                <>
                    <div
                        className="fixed inset-0 z-[80] bg-black/20"
                        onClick={() => setPanel("none")}
                        aria-hidden
                    />
                    <aside
                        className="fixed right-0 top-0 z-[81] flex h-full w-full max-w-md flex-col border-l bg-white shadow-2xl"
                        role="dialog"
                        aria-label={
                            panel === "participants"
                                ? t("constellation.therapist.participantsTitle", "Participants")
                                : t("constellation.therapist.snapshotsTitle")
                        }
                    >
                        <header className="flex h-14 shrink-0 items-center justify-between border-b px-4">
                            <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                                {panel === "participants" ? (
                                    <Users className="h-4 w-4 text-content-tertiary" />
                                ) : (
                                    <Images className="h-4 w-4 text-content-tertiary" />
                                )}
                                {panel === "participants"
                                    ? t("constellation.therapist.participantsTitle", "Participants")
                                    : t("constellation.therapist.snapshotsTitle")}
                            </span>
                            <button
                                type="button"
                                onClick={() => setPanel("none")}
                                aria-label={t("common.close", "Close")}
                                className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </header>
                        <div className="min-h-0 flex-1 overflow-y-auto p-4">
                            {panel === "participants" && board?.session_type != null && (
                                <PresencePanel
                                    participants={participants}
                                    isTherapist={true}
                                    sessionType={board.session_type}
                                    onKick={kickParticipant}
                                    puppets={state.puppets}
                                    onFocusFigure={(id) => {
                                        focusFigure(id);
                                        setPanel("none");
                                    }}
                                />
                            )}
                            {panel === "snapshots" && board && (
                                <ConstellationSnapshotManager
                                    sessionId={board.session_id}
                                    snapshots={board.snapshots}
                                    onRename={renameSnapshot}
                                    onDelete={deleteSnapshot}
                                />
                            )}
                        </div>
                    </aside>
                </>
            )}

            {/* Figure edit menu — actions, emotions, name (✎ button) */}
            {editingPuppet && (
                <BoardOverlay container={view.containerRef.current}>
                <div
                    className="fixed inset-0 z-[80] flex items-end justify-center bg-black/30 sm:items-center"
                    onClick={() => setEditingPuppetId(null)}
                >
                    <div
                        className="w-full max-w-sm rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-base font-semibold">
                                {t("constellation.edit.title", { name: editingPuppet.name })}
                            </h2>
                            <button
                                type="button"
                                onClick={() => setEditingPuppetId(null)}
                                aria-label={t("common.close", "Close")}
                                className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <label className="mb-3 flex flex-col gap-1 text-xs">
                            {t("constellation.builder.name")}
                            <input
                                type="text"
                                value={editingPuppet.name}
                                maxLength={60}
                                onChange={(e) => renamePuppet(editingPuppet.id, e.target.value)}
                                className="rounded-md border px-2 py-1"
                            />
                        </label>

                        {editingIsHuman && (
                            <div className="mb-4 flex flex-col gap-1 text-xs">
                                {t("constellation.builder.action")}
                                <div className="grid grid-cols-2 gap-1">
                                    {Object.values(FigureAction).map((act) => (
                                        <button
                                            key={act}
                                            type="button"
                                            onClick={() => setPuppetAction(editingPuppet.id, act)}
                                            aria-pressed={(editingPuppet.action ?? "stand") === act}
                                            className={`min-h-[36px] rounded-md border px-2 text-sm capitalize ${
                                                (editingPuppet.action ?? "stand") === act
                                                    ? "border-brand-500 bg-brand-50 ring-2 ring-brand-200"
                                                    : "border-gray-100 hover:border-brand-200"
                                            }`}
                                        >
                                            {t(`constellation.action.${act}`)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {editingIsHuman && (
                            <div className="mb-4">
                                <FacePicker
                                    label={t("constellation.builder.editEmotion")}
                                    value={editingPuppet.face}
                                    onChange={(face) => setPuppetFace(editingPuppet.id, face)}
                                />
                            </div>
                        )}

                        <label className="flex flex-col gap-1 text-xs">
                            {t("constellation.builder.note")}
                            <input
                                type="text"
                                value={editingPuppet.note ?? ""}
                                maxLength={60}
                                onChange={(e) => setPuppetNote(editingPuppet.id, e.target.value)}
                                placeholder={t("constellation.builder.notePlaceholder")}
                                className="rounded-md border px-2 py-1"
                            />
                        </label>
                    </div>
                </div>
                </BoardOverlay>
            )}

            {/* Note editor */}
            {editingNote && (
                <BoardOverlay container={view.containerRef.current}>
                <div
                    className="fixed inset-0 z-[80] flex items-end justify-center bg-black/30 sm:items-center"
                    onClick={() => setEditingNoteId(null)}
                >
                    <div
                        className="w-full max-w-sm rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-base font-semibold">{t("constellation.note.editTitle")}</h2>
                            <button
                                type="button"
                                onClick={() => setEditingNoteId(null)}
                                aria-label={t("common.close", "Close")}
                                className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <textarea
                            autoFocus
                            rows={4}
                            value={editingNote.text}
                            onChange={(e) =>
                                dispatch({ type: "editNote", id: editingNote.id, text: e.target.value })
                            }
                            placeholder={t("constellation.note.placeholder")}
                            className="w-full rounded-md border px-2 py-1 text-sm"
                        />
                        <div className="mt-3 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setEditingNoteId(null)}
                                className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover"
                            >
                                {t("common.done", "Done")}
                            </button>
                        </div>
                    </div>
                </div>
                </BoardOverlay>
            )}

            {/* Area editor */}
            {editingZone && (
                <BoardOverlay container={view.containerRef.current}>
                <div
                    className="fixed inset-0 z-[80] flex items-end justify-center bg-black/30 sm:items-center"
                    onClick={() => setEditingZoneId(null)}
                >
                    <div
                        className="w-full max-w-sm rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-base font-semibold">{t("constellation.zone.editTitle")}</h2>
                            <button
                                type="button"
                                onClick={() => setEditingZoneId(null)}
                                aria-label={t("common.close", "Close")}
                                className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <input
                            autoFocus
                            type="text"
                            value={editingZone.label}
                            onChange={(e) =>
                                dispatch({ type: "editZone", id: editingZone.id, label: e.target.value })
                            }
                            placeholder={t("constellation.zone.placeholder")}
                            className="w-full rounded-md border px-2 py-1 text-sm"
                        />
                        <div className="mt-3 flex gap-2">
                            <button
                                type="button"
                                onClick={() => dispatch({ type: "editZone", id: editingZone.id, shape: "circle" })}
                                aria-pressed={editingZone.shape === "circle"}
                                className={`flex min-h-[36px] flex-1 items-center justify-center gap-1 rounded-md border px-2 text-sm ${editingZone.shape === "circle" ? "border-brand-500 bg-brand-50 ring-2 ring-brand-200" : "border-gray-200"}`}
                            >
                                <Circle className="h-4 w-4" /> {t("constellation.table.circle")}
                            </button>
                            <button
                                type="button"
                                onClick={() => dispatch({ type: "editZone", id: editingZone.id, shape: "square" })}
                                aria-pressed={editingZone.shape === "square"}
                                className={`flex min-h-[36px] flex-1 items-center justify-center gap-1 rounded-md border px-2 text-sm ${editingZone.shape === "square" ? "border-brand-500 bg-brand-50 ring-2 ring-brand-200" : "border-gray-200"}`}
                            >
                                <Square className="h-4 w-4" /> {t("constellation.table.square")}
                            </button>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                            {ZONE_COLORS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    aria-label={c}
                                    onClick={() => dispatch({ type: "editZone", id: editingZone.id, color: c })}
                                    style={{ background: c }}
                                    className={`h-7 w-7 rounded-full border-2 ${editingZone.color === c ? "border-gray-800" : "border-white"}`}
                                />
                            ))}
                        </div>
                        <div className="mt-4 flex justify-between">
                            <button
                                type="button"
                                onClick={() => removeZone(editingZone.id)}
                                className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                            >
                                {t("constellation.list.remove", "Delete")}
                            </button>
                            <button
                                type="button"
                                onClick={() => setEditingZoneId(null)}
                                className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover"
                            >
                                {t("common.done", "Done")}
                            </button>
                        </div>
                    </div>
                </div>
                </BoardOverlay>
            )}

            {/* Link editor — name, color, weight for a connection between figures */}
            {editingLink && (
                <LinkEditorModal
                    link={editingLink}
                    onChangeLabel={(label) =>
                        dispatch({ type: "editLink", id: editingLink.id, label })
                    }
                    onChangeColor={(color) =>
                        dispatch({ type: "editLink", id: editingLink.id, color })
                    }
                    onChangeWeight={(weight) =>
                        dispatch({ type: "editLink", id: editingLink.id, weight })
                    }
                    onRemove={() => links.removeLink(editingLink.id)}
                    onClose={links.closeLinkEditor}
                    portalContainer={view.containerRef.current}
                />
            )}

            <SaveSceneModal
                isOpen={sceneModal !== "closed"}
                defaultName={sceneNameSuggestion}
                busy={savingScene || closing === "closing"}
                onConfirm={handleSceneModalConfirm}
                onCancel={handleSceneModalCancel}
                portalContainer={view.containerRef.current}
                centerName={center?.name}
            />

            <AddAreaModal
                isOpen={areaModalOpen}
                onCancel={() => setAreaModalOpen(false)}
                onConfirm={({ shape, label }) => {
                    addZone({ shape, label });
                    setAreaModalOpen(false);
                }}
                portalContainer={view.containerRef.current}
            />

            <ConfirmDialog
                isOpen={confirmEndOpen}
                title={t(endKey)}
                message={t(embedded ? "constellation.therapist.endBoardConfirm" : "constellation.therapist.endSessionConfirm")}
                confirmLabel={t(endKey)}
                destructive
                isBusy={closing === "closing"}
                onConfirm={confirmEndSession}
                onCancel={() => setConfirmEndOpen(false)}
            />

            <BoardStyleModal
                isOpen={boardStyleOpen}
                onClose={() => setBoardStyleOpen(false)}
                shape={state.table.shape ?? "circle"}
                onSetShape={(s) => dispatch({ type: "setTableShape", shape: s })}
                color={state.table.color ?? "#dcf5f4"}
                onSetColor={(c) => dispatch({ type: "setTableColor", color: c })}
                ambiance={state.ambiance ?? "base"}
                onSetAmbiance={(a) => dispatch({ type: "setAmbiance", ambiance: a })}
                portalContainer={view.containerRef.current}
            />
            <ShareLinkDialog
                isOpen={shareOpen}
                onClose={() => setShareOpen(false)}
                title={t("constellation.share.title")}
                subtitle={t("constellation.share.subtitle")}
                status={linkStatus}
                url={linkUrl}
                message={linkUrl ? t("constellation.share.message", { url: linkUrl }) : ""}
                onRetry={() => void mintLink()}
                presence={
                    participants.length > 0
                        ? t("constellation.share.connected", {
                              names: participants.map((p) => p.display_name).join(", "),
                          })
                        : t("constellation.share.waiting")
                }
                onRevoke={handleRevoke}
            />
        </div>
    );
}
